// RudderStack (CDP) transport for the unified event taxonomy.
// RudderStack is the primary analytics SDK — it fans out to GA4 / MoEngage / …
// downstream, so no destination routing lives in app code.
import { RudderAnalytics } from "@rudderstack/analytics-js";
import type { ApiObject, IdentifyTraits } from "@rudderstack/analytics-js";

const WRITE_KEY = process.env.REACT_APP_RUDDERSTACK_WRITE_KEY ?? "";
const DATA_PLANE_URL = process.env.REACT_APP_RUDDERSTACK_DATA_PLANE_URL ?? "";

let instance: RudderAnalytics | null = null;

/** Lazily create + load the RudderStack instance (browser only). */
export function getRudder(): RudderAnalytics | null {
  if (typeof window === "undefined") return null;
  if (!instance) {
    if (!WRITE_KEY || !DATA_PLANE_URL) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] RudderStack write key / data plane URL missing — events dropped.");
      return null;
    }
    instance = new RudderAnalytics();
    instance.load(WRITE_KEY, DATA_PLANE_URL, {});
  }
  return instance;
}

/** Initialise RudderStack as early as possible (call once at app root). */
export function initRudder(): void {
  getRudder();
}

// Which Rupeezy app emitted the event (all apps share one RudderStack source +
// domain, so events are otherwise indistinguishable) and the web form-factor.
const APP_NAME = "rupeezy_login";

// Originating app + web form-factor for this session. `props` (app_name,
// device_type) go on event properties so MoEngage/GA4 receive them as event
// attributes. `context` overrides the RudderStack `app` field (name/namespace/
// version — otherwise the SDK's own identity) and carries device_type. Stable
// for the session, so computed once and reused.
const APP_VERSION = process.env.REACT_APP_COMMIT_VERSION || "";

let cachedMeta: { props: Record<string, string>; context: ApiObject } | null = null;

/** Session metadata: app (props + context.app) + web device form-factor (memoized). */
function eventMeta(): { props: Record<string, string>; context: ApiObject } {
  if (cachedMeta) return cachedMeta;
  const app: ApiObject = { name: APP_NAME, namespace: APP_NAME, version: APP_VERSION };
  const props: Record<string, string> = { app_name: APP_NAME };
  const context: ApiObject = { app };
  if (typeof navigator === "undefined") return { props, context }; // SSR — don't cache, recompute in browser
  const ua = navigator.userAgent || "";
  const uaData = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData;
  let device =
    uaData && typeof uaData.mobile === "boolean"
      ? uaData.mobile
        ? "mobile_web"
        : "desktop_web"
      : /Mobi|Android|iPhone|iPod|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(ua)
        ? "mobile_web"
        : "desktop_web";
  // Best-effort tablet (iPadOS masquerades as desktop Safari).
  if (/iPad/i.test(ua) || (/Android/i.test(ua) && !/Mobi/i.test(ua)) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
    device = "tablet_web";
  }
  props.device_type = device;
  context.device_type = device;
  cachedMeta = { props, context };
  return cachedMeta;
}

export function rudderTrack(event: string, properties: Record<string, unknown>): void {
  const m = eventMeta();
  getRudder()?.track(event, { ...properties, ...m.props } as unknown as ApiObject, { context: { ...m.context } });
}

export function rudderIdentify(userId: string, traits: Record<string, unknown> = {}): void {
  // app_name/device_type are per-event facts, NOT user attributes — kept out of
  // traits so they don't get cached and stamped onto every event's context.traits.
  getRudder()?.identify(userId, traits as unknown as IdentifyTraits, { context: { ...eventMeta().context } });
}

export function rudderReset(): void {
  // Reset userId + traits AND regenerate the anonymousId (the latter is NOT
  // reset by default), so the next user on this browser starts fully clean.
  getRudder()?.reset({ entries: { anonymousId: true } });
}
